import { CombinedGraphQLErrors } from '@apollo/client';

import {
  AuthErrorMessages,
  isMfaSanitizedErrorCode,
  type MfaSanitizedErrorCode,
  ValidationMessages,
} from '@repo/common';
import { getFirstGraphQLErrorMessage, getGraphQLErrorCode } from '@repo/frontend-shared';

export const MFA_REAUTH_REQUIRED_MESSAGE = 'Recent primary reauthentication is required';
export const MFA_OAUTH_REAUTH_REQUIRED_MESSAGE = 'OAuth reauthentication is required';

export type MfaSettingsClientError =
  | { kind: 'reauth' }
  | { kind: 'oauth-reauth' }
  | { kind: 'invalid-password' }
  | { kind: 'sanitized'; code: MfaSanitizedErrorCode; retryAfterSeconds?: number }
  | { kind: 'generic' };

export function readMfaSettingsError(error: unknown): MfaSettingsClientError {
  const candidates = collectErrorCodeCandidates(error);
  for (const candidate of candidates) {
    const code = readSanitizedMfaCode(candidate);
    if (code) {
      const retryAfterSeconds = readRetryAfterSeconds(error);
      return retryAfterSeconds === undefined
        ? { kind: 'sanitized', code }
        : { kind: 'sanitized', code, retryAfterSeconds };
    }
  }

  for (const candidate of candidates) {
    if (
      candidate === ValidationMessages.CURRENT_PASSWORD_INCORRECT ||
      candidate === AuthErrorMessages.INVALID_CREDENTIALS ||
      candidate === 'INVALID_CREDENTIALS' ||
      candidate === 'INVALID_PASSWORD' ||
      candidate.includes(ValidationMessages.CURRENT_PASSWORD_INCORRECT)
    ) {
      return { kind: 'invalid-password' };
    }
  }

  const message = getFirstGraphQLErrorMessage(error, error instanceof Error ? error.message : '');
  if (message.includes(MFA_REAUTH_REQUIRED_MESSAGE)) {
    return { kind: 'reauth' };
  }
  if (message.includes(MFA_OAUTH_REAUTH_REQUIRED_MESSAGE)) {
    return { kind: 'oauth-reauth' };
  }
  return { kind: 'generic' };
}

function collectErrorCodeCandidates(error: unknown): string[] {
  const candidates: string[] = [];
  const push = (value: unknown) => {
    if (typeof value === 'string' && value.length > 0) {
      candidates.push(value);
    }
  };

  push(getGraphQLErrorCode(error));
  push(getFirstGraphQLErrorMessage(error, ''));
  if (error instanceof Error) {
    push(error.message);
  }

  if (CombinedGraphQLErrors.is(error)) {
    collectExtensionCandidates(error.errors[0]?.extensions, push);
  }

  return candidates;
}

function collectExtensionCandidates(extensions: unknown, push: (value: unknown) => void): void {
  if (typeof extensions !== 'object' || extensions === null) {
    return;
  }

  push(Reflect.get(extensions, 'code'));
  push(Reflect.get(extensions, 'errorCode'));
  const details = Reflect.get(extensions, 'details');
  if (typeof details === 'object' && details !== null) {
    push(Reflect.get(details, 'code'));
    push(Reflect.get(details, 'errorCode'));
  }
  collectExtensionCandidates(Reflect.get(extensions, 'originalError'), push);
  collectExtensionCandidates(Reflect.get(extensions, 'exception'), push);
  collectExtensionCandidates(Reflect.get(extensions, 'response'), push);
}

/** How long the server says a refused send must wait, when it says. */
function readRetryAfterSeconds(error: unknown): number | undefined {
  if (!CombinedGraphQLErrors.is(error)) {
    return undefined;
  }
  const details = error.errors[0]?.extensions?.details;
  const retryAfter =
    typeof details === 'object' && details !== null ? Reflect.get(details, 'retryAfterSeconds') : undefined;
  return typeof retryAfter === 'number' && Number.isFinite(retryAfter) && retryAfter > 0
    ? Math.ceil(retryAfter)
    : undefined;
}

function readSanitizedMfaCode(value: string): MfaSanitizedErrorCode | undefined {
  if (isMfaSanitizedErrorCode(value)) {
    return value;
  }
  const match = value.match(/mfa\.[a-z_]+/);
  if (match && isMfaSanitizedErrorCode(match[0])) {
    return match[0];
  }
  return undefined;
}
